/**
 * OcclusionMeshSystem: owns the lifecycle of a scene's invisible depth occluder.
 *
 * Loads the occlusion GLB (defaulting to the collision GLB) via loadOcclusionMesh
 * and destroys it on scene unload so VRAM is freed. Loading is non-fatal: a
 * failure simply leaves `handle` null and occlusion disabled.
 */

import type { Application } from 'playcanvas';
import { loadOcclusionMesh } from './loadOcclusionMesh';
import type { OcclusionHandle } from './loadOcclusionMesh';

class OcclusionMeshSystem {
    private _app: Application;
    private _loadToken = 0;
    handle: OcclusionHandle | null = null;

    constructor(app: Application) {
        this._app = app;
    }

    /** Load the occlusion mesh. Resolves null on failure (occlusion disabled). */
    async load(url: string): Promise<OcclusionHandle | null> {
        this.destroy();
        const token = ++this._loadToken;
        try {
            const handle = await loadOcclusionMesh(this._app, url);
            if (token !== this._loadToken) {
                if (handle) {
                    this._app.root.removeChild(handle.entity);
                    handle.entity.destroy();
                }
                return null;
            }
            this.handle = handle;
        } catch {
            this.handle = null;
        }
        return this.handle;
    }

    /** Destroy the occlusion entity (asset stays cached in the registry). */
    destroy() {
        this._loadToken++;
        const h = this.handle;
        if (!h) return;
        this._app.root.removeChild(h.entity);
        h.entity.destroy();
        this.handle = null;
    }
}

export { OcclusionMeshSystem };
